import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Calendar, MapPin, ChevronRight, X, 
  CreditCard, Info, RefreshCw, Search, Clock, Sparkles, CheckCircle2, ShieldCheck, Tag
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Events() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("Upcoming");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/events").catch(() => api.get("/admin/events"));
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Failed to load global events.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "TBA";
    const start = new Date(startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    const end = new Date(endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${start} - ${end}`;
  };

  const getEventDurationDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 1;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1);
  };

  const getRecommendedPlan = (days) => {
    if (days <= 3) {
      return {
        id: "event_lite",
        name: "Event Lite Plan",
        price: 2299,
        credits: 350,
        badge: "Best for 2-3 Days Event"
      };
    } else {
      return {
        id: "event_pro",
        name: "Event Pro Plan",
        price: 6500,
        credits: 2000,
        badge: "Best for Multi-Day Event"
      };
    }
  };

  const isEventSubscribed = useCallback((eventId) => {
    if (!user || !user.subscription_active) return false;
    return String(user.event_id) === String(eventId);
  }, [user]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return events.filter((ev) => {
      const startDate = ev.start_date ? new Date(ev.start_date) : null;
      const endDate = ev.end_date ? new Date(ev.end_date) : null;

      if (startDate) startDate.setHours(0, 0, 0, 0);
      if (endDate) endDate.setHours(23, 59, 59, 999);

      const isLive = startDate && endDate && today >= startDate && today <= endDate;
      const isUpcoming = startDate && startDate > today;
      const isPast = endDate && endDate < today;

      let matchesTab = false;
      if (activeTab === "Subscribed") {
        matchesTab = isEventSubscribed(ev.id);
      } else if (activeTab === "Live") {
        matchesTab = isLive;
      } else if (activeTab === "Upcoming") {
        matchesTab = isUpcoming;
      } else if (activeTab === "Past") {
        matchesTab = isPast;
      } else {
        matchesTab = true;
      }

      const matchesSearch = !q || (
        (ev.title || "").toLowerCase().includes(q) ||
        (ev.location || "").toLowerCase().includes(q) ||
        (ev.description || "").toLowerCase().includes(q)
      );

      return matchesTab && matchesSearch;
    });
  }, [events, activeTab, searchQuery, isEventSubscribed]);

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 pb-28 font-sans">
      <div className="max-w-md mx-auto px-5 pt-8 pb-4">
        
        {/* HEADER */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Global Events</h1>
          <button 
            onClick={fetchEvents}
            className="p-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition shadow-sm cursor-pointer"
            title="Refresh Events"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-blue-600" : ""}`} />
          </button>
        </div>

        {/* SEARCH */}
        <div className="relative mb-4">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search events by name, location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-medium text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600 transition shadow-sm"
          />
        </div>

        {/* TABS */}
        <div className="grid grid-cols-4 gap-1.5 bg-slate-200/60 p-1.5 rounded-2xl mb-6">
          {["Subscribed", "Live", "Upcoming", "Past"].map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2 rounded-xl text-[11px] font-bold transition-all cursor-pointer truncate text-center ${
                  isActive
                    ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                    : "text-slate-600 hover:text-slate-900 hover:bg-white/50"
                }`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* EVENTS LIST */}
        <div className="space-y-4">
          {loading ? (
            <div className="py-16 text-center text-slate-400 text-xs font-bold animate-pulse">
              Syncing global trade shows...
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="py-16 bg-white border border-slate-200/80 rounded-3xl text-center space-y-2 p-6 shadow-sm">
              <Calendar className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-xs font-bold text-slate-600">
                {activeTab === "Subscribed"
                  ? "No subscribed events found"
                  : `No ${activeTab.toLowerCase()} events found`}
              </p>
            </div>
          ) : (
            filteredEvents.map((event) => {
              const durationDays = getEventDurationDays(event.start_date, event.end_date);
              const recPlan = getRecommendedPlan(durationDays);
              const subscribed = isEventSubscribed(event.id);
              const isPharma = (event.title || "").toLowerCase().includes("pharma");

              return (
                <div
                  key={event.id}
                  className={`bg-white border rounded-3xl p-5 shadow-sm space-y-4 transition ${
                    subscribed
                      ? "border-emerald-500/80 ring-2 ring-emerald-500/10"
                      : "border-slate-200/90 hover:border-blue-500/40"
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-100">
                          {durationDays} Day{durationDays > 1 ? "s" : ""} Event
                        </span>
                        {isPharma && !subscribed && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                            <Tag className="w-2.5 h-2.5" /> 50% Off
                          </span>
                        )}
                      </div>

                      {subscribed && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Subscribed
                        </span>
                      )}
                    </div>

                    <h2 className="text-xl font-bold text-slate-900 tracking-tight leading-snug">
                      {event.title}
                    </h2>

                    <div className="space-y-1 text-xs text-slate-500 font-medium">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                        <span>{formatDateRange(event.start_date, event.end_date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-rose-500 shrink-0" />
                        <span className="capitalize">{event.location}</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                    {subscribed ? (
                      <span className="text-[11px] font-bold text-emerald-700 flex items-center gap-1">
                        <ShieldCheck className="w-4 h-4 text-emerald-600" />
                        Pass Active ({user?.plan_name})
                      </span>
                    ) : (
                      <span className="text-[11px] font-semibold text-slate-500">
                        {isPharma ? (
                          <>
                            Rec. Plan: <span className="line-through text-slate-400">₹{recPlan.price}</span>{" "}
                            <strong className="text-emerald-600 font-black">₹{Math.round(recPlan.price * 0.5)}</strong>
                          </>
                        ) : (
                          <>
                            Rec. Plan: <strong className="text-slate-900">₹{recPlan.price}</strong>
                          </>
                        )}
                      </span>
                    )}

                    <button
                      onClick={() => setSelectedEvent(event)}
                      className={`px-4 py-2 text-xs font-bold rounded-xl transition flex items-center gap-1 cursor-pointer ${
                        subscribed
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-blue-50 hover:bg-blue-100 text-blue-600"
                      }`}
                    >
                      <span>{subscribed ? "View Pass" : "View Details"}</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

      </div>

      {/* EVENT DETAILS MODAL */}
      {selectedEvent && (() => {
        const durationDays = getEventDurationDays(selectedEvent.start_date, selectedEvent.end_date);
        const recPlan = getRecommendedPlan(durationDays);
        const subscribed = isEventSubscribed(selectedEvent.id);
        const isEventPast =
          (selectedEvent.status || "").toLowerCase() === "past" ||
          (selectedEvent.status || "").toLowerCase() === "expired" ||
          activeTab === "Past";

        const isPharmaEvent =
          (selectedEvent.title || "").toLowerCase().includes("pharma") ||
          (selectedEvent.description || "").toLowerCase().includes("pharma");
        const promoCoupon = isPharmaEvent ? "PHARMA50" : null;
        const discountRate = promoCoupon ? 0.5 : 0;
        const finalPrice = discountRate > 0 ? Math.round(recPlan.price * (1 - discountRate)) : recPlan.price;

        return (
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto relative border border-slate-100">
              
              <button
                onClick={() => setSelectedEvent(null)}
                className="absolute top-5 right-5 p-2 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="space-y-2 pr-8">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                    isEventPast ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-blue-50 text-blue-600 border-blue-100"
                  }`}>
                    {durationDays} Days Duration • {selectedEvent.status || activeTab}
                  </span>

                  {subscribed && (
                    <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Active Pass
                    </span>
                  )}
                </div>

                <h2 className="text-2xl font-black text-slate-900 leading-tight">
                  {selectedEvent.title}
                </h2>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3.5 bg-slate-50 rounded-2xl border border-slate-100 text-xs">
                <div className="space-y-1">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Dates</span>
                  <p className="font-semibold text-slate-800 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-blue-600" />
                    {formatDateRange(selectedEvent.start_date, selectedEvent.end_date)}
                  </p>
                </div>

                <div className="space-y-1">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Location</span>
                  <p className="font-semibold text-slate-800 flex items-center gap-1 capitalize">
                    <MapPin className="w-3 h-3 text-rose-500" />
                    {selectedEvent.location}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Info className="w-3.5 h-3.5 text-blue-600" /> Overview
                </h3>
                <p className="text-xs text-slate-600 leading-relaxed bg-slate-50/80 p-4 rounded-2xl border border-slate-100 font-medium">
                  {selectedEvent.description || "No specific description provided for this exhibition."}
                </p>
              </div>

              {subscribed ? (
                <div className="space-y-3 pt-2">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl space-y-1.5 text-center">
                    <div className="inline-flex items-center justify-center p-2 bg-emerald-100 text-emerald-800 rounded-full mb-1">
                      <CheckCircle2 className="w-6 h-6" />
                    </div>
                    <h4 className="font-extrabold text-emerald-950 text-sm">You are Subscribed!</h4>
                    <p className="text-xs text-emerald-700 font-medium">
                      Your <strong>{user?.plan_name}</strong> pass is active for this trade show. You can scan business cards directly from the app.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedEvent(null);
                      navigate("/capture");
                    }}
                    className="w-full py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-sm rounded-2xl shadow-lg shadow-emerald-600/25 transition cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>Start Scanning Leads</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ) : isEventPast ? (
                <div className="pt-2">
                  <div className="w-full py-3.5 bg-slate-100 border border-slate-200 text-slate-500 font-bold text-xs rounded-2xl text-center flex items-center justify-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>Event Has Expired (Registration Closed)</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 pt-2">
                  {/* SPECIAL PHARMA COUPON PROMO */}
                  {promoCoupon && (
                    <div className="flex items-center justify-between p-3.5 bg-amber-50/90 border border-amber-200 rounded-2xl text-amber-950">
                      <div className="flex items-center gap-2.5">
                        <div className="p-2 bg-amber-100 text-amber-800 rounded-xl">
                          <Tag className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="text-xs font-black">Special 50% Event Discount</p>
                          <p className="text-[10px] text-amber-700 font-semibold">
                            Promo code <span className="font-mono font-bold bg-amber-200/80 px-1 py-0.5 rounded text-amber-900">{promoCoupon}</span> applied
                          </p>
                        </div>
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-wider bg-amber-200 text-amber-950 px-2 py-1 rounded-lg border border-amber-300">
                        50% OFF
                      </span>
                    </div>
                  )}

                  {/* PLAN DETAILS */}
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50/50 border border-blue-200/80 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-black uppercase tracking-wider text-blue-700 bg-white px-2 py-0.5 rounded-md border border-blue-100 shadow-2xs">
                        {recPlan.badge}
                      </span>
                      <Sparkles className="w-4 h-4 text-blue-600" />
                    </div>

                    <div className="flex items-baseline justify-between pt-1">
                      <div>
                        <h4 className="font-extrabold text-slate-900 text-sm">{recPlan.name}</h4>
                        <p className="text-[11px] text-slate-500 font-medium">
                          {typeof recPlan.credits === "number" ? `${recPlan.credits.toLocaleString()} Scans & WhatsApp Messages` : recPlan.credits}
                        </p>
                      </div>

                      <div className="text-right">
                        {promoCoupon ? (
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-xs text-slate-400 line-through font-semibold">₹{recPlan.price}</span>
                            <span className="text-lg font-black text-emerald-600">₹{finalPrice}</span>
                          </div>
                        ) : (
                          <span className="text-lg font-black text-blue-900">₹{recPlan.price}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* GET PASS CTA */}
                  <button
                    onClick={() => {
                      setSelectedEvent(null);
                      const queryParams = new URLSearchParams({
                        plan: recPlan.id,
                        eventId: selectedEvent.id,
                        ...(promoCoupon ? { coupon: promoCoupon } : {})
                      });
                      navigate(`/payment?${queryParams.toString()}`);
                    }}
                    className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-black text-sm rounded-2xl shadow-lg shadow-blue-600/25 transition cursor-pointer flex items-center justify-center gap-2"
                  >
                    <CreditCard className="w-4 h-4" />
                    <span>Get Pass ({recPlan.name})</span>
                  </button>
                </div>
              )}

            </div>
          </div>
        );
      })()}

    </div>
  );
}